<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col sm="12">
        <div class="date-picker">
          <date-range-picker @rangeUpdated="rangeUpdated"></date-range-picker>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col sm="12">
        <b-card>
          <div slot="header">
            <strong>Active Users</strong>
          </div>
          <b-row>
            <b-col md="6" class="my-1">
              <div class="d-flex">
                <div class="d-flex">
                  <div class="pt-1">Per Page:</div>
                  <b-form-group class="mb-0 pl-2">
                    <b-form-select :options="pageOptions" v-model="perPage" class="mb-1" />
                  </b-form-group>
                </div>
              </div>
            </b-col>
            <b-col md="6" class="my-1">
              <div class="float-right">
                <div class="d-flex">
                  <div class="pt-1">Search:</div>
                  <b-form-group class="mb-0 pl-2">
                    <b-input-group class="mb-1">
                      <b-form-input v-model="filter" placeholder="Type to Search"  />
                    </b-input-group>
                  </b-form-group>
                </div>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col lg="12">
              <b-card>
                <!-- Main table element -->
                <b-table show-empty
                         stacked="md"
                         :items="usage"
                         :fields="fields"
                         :current-page="currentPage"
                         :per-page="perPage"
                         :filter="filter"
                         :sort-by.sync="sortBy"
                         :sort-desc.sync="sortDesc"
                         :sort-direction="sortDirection"
                         @filtered="onFiltered"
                >
                  <template slot="user" slot-scope="data">
                    <router-link :to="'users/' + data.value.user_id">
                      {{data.value.name}}
                    </router-link><br />
                  </template>
                  <template slot="address" slot-scope="data">
                    {{data.value.street_address}}
                    {{data.value.city}},
                    {{data.value.province}}
                    {{data.value.postal_code}}
                  </template>
                  <template slot="company_name" slot-scope="row">{{row.value}}</template>
                  <template slot="time" slot-scope="row">{{row.value | moment}}</template>
                </b-table>

                <b-row>
                  <b-col md="6" class="my-1">
                    Showing {{showingFrom}} - {{showingTo}} of {{totalRows}} records
                  </b-col>
                  <b-col md="6" class="my-1">
                    <div class="float-right">
                      <b-pagination :total-rows="totalRows" :per-page="perPage" v-model="currentPage" class="my-0" />
                    </div>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <loading :active.sync="isLoading"
      :is-full-page="true"></loading>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import Loading from 'vue-loading-overlay'
import DateRangePicker from '../widgets/DateRangePicker'
import getAdmin from './../../mixins/getAdmin'
import onFiltered from './../../mixins/onFiltered'
import showingFrom from './../../mixins/showingFrom'
import showingTo from './../../mixins/showingTo'
import sortOptions from './../../mixins/sortOptions'

export default {
  name: 'DeviceUsage',
  mixins: [
    getAdmin,
    onFiltered,
    showingFrom,
    showingTo,
    sortOptions
  ],
  components: {
    Loading,
    DateRangePicker
  },
  data () {
    return {
      admin: {},
      fields: [
        { key: 'user', label: 'User' },
        { key: 'address', label: 'Location' },
        { key: 'company_name', label: 'Company' },
        { key: 'time', label: 'Time', sortable: true }
      ],
      currentPage: 1,
      filter: null,
      perPage: 10,
      totalRows: 0,
      pageOptions: [ 10, 15, 20, 25, 50 ],
      sortBy: null,
      sortDesc: false,
      sortDirection: 'asc',
      usage: []
    }
  },
  computed: {
    ...mapGetters([
      'isLoading'
    ])
  },
  methods: {
    /**
     * Formats device usage data into array of objects to be rendered in the Bootstrap-vue table component.
     * @param {array} data - Data array of visits.
     */
    getDeviceUsage: async function (data) {
      let newData = []
      let userIds = []

      for (let i = 0; i < data.length; i++) {
        if (userIds.includes(data[i].user_id)) {
          continue
        } else {
          userIds.push(data[i].user_id)
        }

        newData.push({
          device_id: data[i].device_id,
          user: {
            user_id: data[i].user_id,
            name: this.admin.level <= 1 ? data[i].first_name + ' ' + data[i].last_name : '*******'
          },
          company_name: data[i].company_name,
          address: {
            city: data[i].city,
            street_address: data[i].street_address,
            province: data[i].province,
            postal_code: data[i].postal_code
          },
          time: data[i].time
        })
      }

      return newData
    },
    /**
     * Queries data from the visits controller of the backend api with the parameters 'begin' and 'end' which represent the beginning and end dates inclusively you want to get device visit data for.
     * @param {string} selection - Parameter to determine whether to select all or only authenticated items.
     */
    getVisitData: function (selection = 'auth') {
      let self = this
      this.$store.commit('updateIsLoading', true)

      let begin = moment(this.$store.state.startDate).format('YYYY-MM-DD')
      let end = moment(this.$store.state.endDate).format('YYYY-MM-DD')

      axios.get(`${process.env.VUE_APP_API_URL}api/visits?selection=${selection}&begin=${begin}&end=${end}`, {
        headers: {
          'x-access-token': localStorage.getItem('user-token')
        }
      })
        .then(async function (response) {
          self.usage = await self.getDeviceUsage(response.data.data)
          self.totalRows = self.usage.length

          self.$store.commit('updateIsLoading', false)
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /**
     * Updates getVisitData when the 'rangeUpdated' event is emitted from the DateRangePicker component whenever a new date is selected.
     */
    rangeUpdated: function () {
      this.getVisitData()
    }
  },
  filters: {
    moment: function (date) {
      return moment(date).format('MMMM Do YYYY, h:mm:ss a')
    }
  },
  async mounted () {
    await this.getAdmin()

    if (this.admin.level === 0) {
      await this.getVisitData('all')
    } else {
      await this.getVisitData()
    }
  }
}
</script>
